import { Badge, useIsDark, type BadgeTone, Tooltip } from '@highlight/ui'
import type { StagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { resolveContactAvatarSrc } from '@renderer/utils/contact-avatar'
import { memo, Fragment } from 'react'
import { twMerge } from 'tailwind-merge'
import { AnimatedEmptyIcon } from './animated-empty-icon'

interface ActionTagBadgeProps {
  tags: StagedActionView['tags']
  keyName: string
  editable?: boolean
  singleLine?: boolean
}

/** The integration's tag colours, as Badge tones. */
const TAG_TONES: Record<string, BadgeTone> = {
  gray: 'neutral',
  brown: 'brown',
  orange: 'warning',
  yellow: 'yellow',
  green: 'success',
  blue: 'info',
  purple: 'purple',
  pink: 'pink',
  red: 'danger',
}

function tagTone(color: string | undefined): BadgeTone {
  return (color && TAG_TONES[color]) || 'neutral'
}

/**
 * An editable tag sits inside a picker's trigger, so it takes the control look. A read-only tag is
 * a borderless wash, so it never reads as something to press.
 */
function frameVariant(editable: boolean) {
  return editable ? 'outline' : 'subtle'
}

const EMOJI_RE = /\p{Extended_Pictographic}/u

export function Icon({ src, darkSrc, className }: { src?: string; darkSrc?: string; className?: string }) {
  const isDark = useIsDark()
  // Punting: will handle unresolved URLs later
  // const [errored, setErrored] = useState(false)
  const value = resolveContactAvatarSrc(isDark && darkSrc ? darkSrc : src)
  if (!value) return null
  if (!/^(https?:|data:|\/)/i.test(value) && EMOJI_RE.test(value)) {
    return <span className={className}>{value}</span>
  }
  return <img src={value} alt="" className={className} />
}

function truncateTagValue(value: string) {
  if (value.length > 15) {
    return value.slice(0, 15) + '…'
  }
  return value
}

interface TagValueProps {
  tag: StagedActionView['tags'][number]
  singleLine?: boolean
}
function TagValue({ tag, singleLine = false }: TagValueProps) {
  if (tag.previousValue) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        <span
          className={twMerge(
            'text-foreground-placeholder line-through',
            // Bounded layouts (the drawer) truncate the prior value so the pill fits;
            // the single-line chat preview scrolls instead, so keep it full-width.
            singleLine ? 'whitespace-nowrap' : 'min-w-0 max-w-[120px] shrink truncate',
          )}
        >
          {tag.previousValue}
        </span>
        <span className="text-foreground-placeholder shrink-0">→</span>
        <Icon src={tag.icon} darkSrc={tag.darkModeIcon} className="shrink-0" />
        <span className={twMerge(singleLine ? 'whitespace-nowrap' : 'min-w-0 max-w-[120px] shrink-0 truncate')}>
          {tag.value}
        </span>
      </span>
    )
  }
  if (tag.breadcrumb?.length) {
    return tag.breadcrumb.slice(-2).map((part, idx) => (
      <Fragment key={idx}>
        {idx !== 0 && <span className="text-foreground-placeholder">/</span>}
        <span className="flex gap-1">
          <Icon src={part.icon} darkSrc={part.darkModeIcon} />
          <span>{truncateTagValue(part.title)}</span>
        </span>
      </Fragment>
    ))
  }
  return <span>{truncateTagValue(tag.value)}</span>
}

export const ActionTagBadge = memo(function ActionTagBadge({
  tags,
  keyName,
  editable = false,
  singleLine = false,
}: ActionTagBadgeProps) {
  // somewhat deprecated because the design isn't very good
  if (tags.length > 1) {
    return (
      <Badge variant={frameVariant(editable)} shape="squircle">
        <span className="text-foreground-secondary">{tags[0].key}</span>
        {tags.map((tag) => {
          const pill = (
            <Badge key={tag.value} size="xs" tone={tagTone(tag.color)}>
              {!tag.breadcrumb?.length && <Icon src={tag.icon} darkSrc={tag.darkModeIcon} />}
              <TagValue tag={tag} singleLine={singleLine} />
            </Badge>
          )
          if (tag.tooltip) {
            return (
              // Read-only tag areas set `pointer-events-none` (see ActionTagArea), which
              // would otherwise swallow the hover the tooltip trigger needs to open.
              <Tooltip key={tag.value}>
                <Tooltip.Trigger className="pointer-events-auto" render={pill} />
                <Tooltip.Content>{tag.tooltip}</Tooltip.Content>
              </Tooltip>
            )
          }
          return pill
        })}
      </Badge>
    )
  }

  if (tags.length === 0) {
    return (
      <Badge
        variant={frameVariant(editable)}
        shape="squircle"
        // An empty slot to fill: the edge goes dashed when there's a picker behind it.
        className={twMerge('text-foreground-placeholder', editable && 'border-border border border-dashed shadow-none')}
      >
        <AnimatedEmptyIcon editable={editable} className="size-3" />
        <span>{keyName}</span>
      </Badge>
    )
  }

  const tag = tags[0]

  const badge = (
    <Badge
      variant={frameVariant(editable)}
      tone={tagTone(tag.color)}
      shape="squircle"
      // In bounded (wrap) layouts a transition pill may be wide; let it shrink so the
      // previous value truncates rather than overflowing when it takes the row's full
      // width. The single-line chat preview scrolls instead, so it keeps natural width.
      className={!singleLine && tag.previousValue ? 'shrink' : undefined}
    >
      {tag.alwaysShowKey && <span className="text-foreground-secondary">{tag.key}</span>}
      {!tag.breadcrumb?.length && (
        <Icon
          src={tag.previousValue ? tag.previousIcon : tag.icon}
          darkSrc={tag.previousValue ? tag.previousDarkModeIcon : tag.darkModeIcon}
        />
      )}
      <TagValue tag={tag} singleLine={singleLine} />
    </Badge>
  )

  if (tag.tooltip) {
    return (
      // `pointer-events-auto` re-enables hover inside read-only (`pointer-events-none`) tag areas.
      <Tooltip>
        <Tooltip.Trigger className="pointer-events-auto" render={badge} />
        <Tooltip.Content>{tag.tooltip}</Tooltip.Content>
      </Tooltip>
    )
  }

  return badge
})
